/**
 * `ApiProblem` — RFC 9457 as the clients see it (04-architecture § 8.2, § 10.3).
 *
 * **Switch on `code`, never on the HTTP status.** Status values differ between
 * the server doc and this one; the code is the contract.
 */

/** Every code of `cowoo-server/docs/05-api.md` § 1, plus the three client-side ones. */
export const API_ERROR_CODES = [
  'validation_failed',
  'session_invalid',
  'auth_unavailable',
  'forbidden',
  'not_admin',
  'not_found',
  'conflict',
  'idempotency_conflict',
  'rate_limited',
  'billing_profile_required',
  'age_restricted',
  'account_blocked',
  'payment_method_required',
  'payment_failed',
  'payment_action_required',
  'already_member',
  'desk_taken',
  'desk_reserved',
  'desk_occupied',
  'desk_unavailable',
  'already_checked_in',
  'hold_expired',
  'booking_limit_reached',
  'advance_window_exceeded',
  'priority_window',
  'location_full',
  'location_not_open',
  'cancellation_deadline_passed',
  'trial_not_eligible',
  'no_locker_available',
  'guest_limit_reached',
  'team_budget_exceeded',
  'contract_active',
  'publish_conflicts',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];
export type ClientErrorCode = 'network_error' | 'timeout' | 'unknown';
export type ProblemCode = ApiErrorCode | ClientErrorCode;

const KNOWN = new Set<string>(API_ERROR_CODES);

export interface DeskAlternative {
  resourceId: string;
  label: string;
  zone: string | null;
  hint?: string;
}

export interface CurrentBooking {
  bookingId: string;
  resourceId: string;
  label: string;
  zone: string | null;
  locationId: string;
}

export interface ProblemExtensions {
  /** § 5.15.1 — nearby free desks offered after a failed check-in. */
  label?: string;
  stripeCode?: string;
  clientSecret?: string;
  alternatives?: DeskAlternative[];
  currentBooking?: CurrentBooking;
  /** 10 § 6.6 — the draft revision the server holds when a save is stale. */
  rev?: number;
  conflicts?: unknown[];
  /** § 5.2 — the bookings a resource block would sit on top of. */
  bookings?: { id: string; day: string }[];
  retryAfterSeconds?: number;
}

export class ApiProblem extends Error {
  override readonly name = 'ApiProblem';

  constructor(
    /** HTTP status; `0` means network error or timeout. */
    readonly status: number,
    readonly code: ProblemCode,
    readonly title?: string,
    override readonly message: string = code,
    readonly detail?: string,
    readonly fieldErrors: { path: string; code: string }[] = [],
    readonly ext: ProblemExtensions = {},
  ) {
    super(code);
  }

  /** Never throws: a non-problem response becomes `unknown`. */
  static async fromResponse(response: Response): Promise<ApiProblem> {
    // A non-problem response (an HTML error page, an empty body) must not throw
    // here: it becomes the generic `unknown` code instead.
    const body = await response
      .clone()
      .json()
      .then((value) => value as Record<string, unknown>)
      .catch(() => ({}) as Record<string, unknown>);
    const rawCode = typeof body.code === 'string' ? body.code : 'unknown';
    const code: ProblemCode = KNOWN.has(rawCode) ? (rawCode as ApiErrorCode) : 'unknown';
    const retryAfter = response.headers.get('retry-after');
    return new ApiProblem(
      response.status,
      code,
      typeof body.title === 'string' ? body.title : undefined,
      rawCode,
      typeof body.detail === 'string' ? body.detail : undefined,
      Array.isArray(body.errors) ? (body.errors as { path: string; code: string }[]) : [],
      {
        ...(typeof body.stripeCode === 'string' ? { stripeCode: body.stripeCode } : {}),
        ...(typeof body.clientSecret === 'string' ? { clientSecret: body.clientSecret } : {}),
        ...(Array.isArray(body.alternatives) ? { alternatives: body.alternatives as DeskAlternative[] } : {}),
        ...(body.currentBooking ? { currentBooking: body.currentBooking as CurrentBooking } : {}),
        ...(Array.isArray(body.conflicts) ? { conflicts: body.conflicts } : {}),
        ...(typeof body.rev === 'number' ? { rev: body.rev } : {}),
        ...(retryAfter ? { retryAfterSeconds: Number(retryAfter) } : {}),
      },
    );
  }

  /** Turns a thrown `fetch` failure into a problem the UI can render. */
  static fromThrown(error: unknown): ApiProblem {
    if (error instanceof ApiProblem) return error;
    const name = (error as { name?: string } | null)?.name;
    if (name === 'AbortError' || name === 'TimeoutError') return new ApiProblem(0, 'timeout');
    return new ApiProblem(0, 'network_error');
  }

  /** `true` when the server code is newer than this build knows (report once). */
  get isUnmapped(): boolean {
    return this.code === 'unknown' && this.status >= 400;
  }

  get i18nKey(): string {
    return `errors.${this.code}`;
  }

  /** Screens may ask for a more specific key, e.g. `errors.booking_limit_reached.desk_day`. */
  i18nKeyWithContext(context: string): string {
    return `${this.i18nKey}.${context}`;
  }
}

export function isApiProblem(value: unknown): value is ApiProblem {
  return value instanceof ApiProblem;
}

/**
 * 04 § 15 — 4xx problems are expected and are not reported to Sentry, except
 * `idempotency_conflict` (a client bug) and unmapped codes.
 */
export function shouldReport(problem: ApiProblem): boolean {
  if (problem.code === 'idempotency_conflict') return true;
  if (problem.isUnmapped) return true;
  return problem.status >= 500;
}
